import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const checkout = async (plan) => {
    if (!user) { navigate("/signup"); return; }
    if (user.role === "admin") { toast.info("Admin already has unlimited access"); return; }
    setLoading(plan);
    try {
      const r = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b-2 border-black bg-white sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FF4F00] border-2 border-black flex items-center justify-center font-display font-black text-white">V</div>
            <span className="font-display font-black text-2xl tracking-tight">VIBELEAD</span>
          </Link>
          <div className="flex gap-3">
            {user ? (
              <Link to="/dashboard" className="btn-brutal-outline !px-4 !py-2 text-sm">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="btn-brutal-outline !px-4 !py-2 text-sm">Log in</Link>
                <Link to="/signup" className="btn-brutal !px-4 !py-2 text-sm">Start Free</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="py-16 md:py-24 dot-grid">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="label-cap mb-3">// Pricing</div>
            <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-4 leading-[0.95]">How serious are you<br />about your list?</h1>
            <p className="text-lg text-zinc-600 italic">Three paths. All of them say yes. The only question is which yes is yours.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free */}
            <div className="card-brutal !p-8 flex flex-col" data-testid="plan-free">
              <div className="label-cap mb-2">Starter</div>
              <h3 className="font-display font-black text-2xl mb-1">Prove It To Me First.</h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display font-black text-4xl">$0</span>
                <span className="text-zinc-500 text-sm">forever</span>
              </div>
              <div className="text-zinc-500 mb-6 text-sm italic">5 pages is enough to know. Most people upgrade within a week.</div>
              <ul className="space-y-2 text-sm mb-6 flex-1">
                <Item>5 lead capture pages</Item>
                <Item>All content delivery formats</Item>
                <Item>Full opt-in and delivery automation</Item>
                <Item>VibeLead branding</Item>
              </ul>
              <button onClick={() => navigate(user ? "/dashboard" : "/signup")} className="btn-brutal-outline w-full" data-testid="plan-free-cta">
                {user ? "Continue Free" : "Start Free — No Card Required"}
              </button>
            </div>

            {/* Pro Monthly */}
            <div className="card-brutal !p-8 flex flex-col" data-testid="plan-monthly">
              <div className="label-cap mb-2">Pro Monthly</div>
              <h3 className="font-display font-black text-2xl mb-1">I'm Building Now.</h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display font-black text-4xl">$27</span>
                <span className="text-zinc-500 text-sm">/ month</span>
              </div>
              <div className="text-zinc-500 mb-6 text-sm italic">Less than one lost subscriber is worth to a serious operator.</div>
              <ul className="space-y-2 text-sm mb-6 flex-1">
                <Item>Up to 30 lead pages per month</Item>
                <Item>Unlimited subscribers</Item>
                <Item>All delivery formats</Item>
                <Item>Custom domain</Item>
                <Item>No VibeLead branding</Item>
                <Item>Priority support</Item>
              </ul>
              <button onClick={() => checkout("monthly")} disabled={loading === "monthly"} className="btn-brutal-outline w-full" data-testid="plan-monthly-cta">
                {loading === "monthly" ? "Loading…" : "Go Pro Monthly"}
              </button>
            </div>

            {/* Pro Yearly - featured */}
            <div className="card-brutal !p-8 relative bg-black text-white border-black flex flex-col" data-testid="plan-yearly">
              <div className="absolute -top-3 -right-3 bg-[#FF4F00] border-2 border-white px-3 py-1 font-bold text-xs">SAVES $235</div>
              <div className="label-cap !text-zinc-400 mb-2 flex items-center gap-2"><Crown size={12} className="text-[#FF4F00]" /> THE ONLY LOGICAL CHOICE</div>
              <h3 className="font-display font-black text-2xl mb-1">I'm Not Playing Around.</h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display font-black text-4xl">$89</span>
                <span className="text-zinc-400 text-sm">/ year</span>
              </div>
              <div className="text-zinc-300 mb-6 text-sm italic">24 cents a day. The people who choose yearly already know.</div>
              <ul className="space-y-2 text-sm mb-6 flex-1">
                <Item white>Everything in Pro Monthly</Item>
                <Item white>Saves $235 vs monthly</Item>
                <Item white>Lock the price for 12 months</Item>
              </ul>
              <button onClick={() => checkout("yearly")} disabled={loading === "yearly"} className="w-full px-5 py-3 font-bold border-2 border-white bg-[#FF4F00] text-white" style={{ boxShadow: "4px 4px 0 #fff" }} data-testid="plan-yearly-cta">
                {loading === "yearly" ? "Loading…" : "Lock In $89/Year"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Item({ children, white }) {
  return <li className="flex items-start gap-2"><Check size={16} className={`mt-0.5 ${white ? "text-[#FF4F00]" : "text-[#FF4F00]"}`} /> <span>{children}</span></li>;
}
